// cart-shipping-protection.js
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SP_OPT_OUT_KEY = 'sp-opted-out';

class CartShippingProtection {
  constructor() {
    this.variantList = [];
    this._autoAddDone = false;
    this._autoAddInProgress = false; // Debounce concurrent /cart.js fetches
    this._pendingAdd = null;     // Promise that resolves when SP auto-add is complete
    this._resolvePending = null; // Resolver for _pendingAdd
    this.toggleSelector = 'input.switch[name="attributes[shipping-protection]"]';
    this.rescan = this.rescan.bind(this);

    document.addEventListener('change', this._onChange.bind(this), true);
    document.addEventListener('click', this._onAddToCartClick.bind(this), true);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate.bind(this));
    document.addEventListener('shopify:section:load', this.rescan);
    // Intercept checkout form submit — if SP auto-add is still in flight, wait for it first
    document.addEventListener('submit', this._interceptCheckout.bind(this), true);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.rescan, { once: true });
    } else {
      this.rescan();
    }

    // Rescan when cart DOM is replaced (e.g. after morph or drawer open)
    this._mo = new MutationObserver(() => queueMicrotask(this.rescan));
    this._mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  rescan() {
    const hadVariants = this.variantList.length > 0;
    const ids = new Set();
    document.querySelectorAll('[data-variantid]').forEach((el) => {
      const id = Number(el.getAttribute('data-variantid'));
      if (Number.isFinite(id)) ids.add(id);
    });
    this.variantList = Array.from(ids);

    // On first variant discovery, check if cart already has items (page-load case).
    if (!hadVariants && this.variantList.length > 0 && !this._autoAddDone) {
      this._autoAddDone = true;
      this._autoAddFromCart();
    }
  }

  /**
   * Fetches the current cart and auto-adds SP if needed.
   * Used for: (1) page-load when cart already has items, (2) add-to-cart events
   * where the event doesn't carry full cart data (CartAddEvent from product form).
   * Guarded against concurrent fetches.
   */
  async _autoAddFromCart() {
    if (sessionStorage.getItem(SP_OPT_OUT_KEY)) return;
    if (this._autoAddInProgress) return;
    this._autoAddInProgress = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      await this._maybeAddSP(cart);
    } catch (_) {}
    this._autoAddInProgress = false;
  }

  /**
   * Core auto-add logic. Shared by both the page-load path and the cart-update path.
   */
  async _maybeAddSP(cart) {
    if (sessionStorage.getItem(SP_OPT_OUT_KEY)) return;
    if (!this.variantList.length) return;

    const nonSPItems = (cart.items ?? []).filter((item) => !this.variantList.includes(item.id));
    if (!nonSPItems.length) return; // empty or SP-only cart

    // Already present — just sync the toggle
    if ((cart.items ?? []).some((item) => this.variantList.includes(item.id))) {
      this._syncToggles(true);
      return;
    }

    this._syncToggles(true);
    const params = new URLSearchParams();
    params.append(`updates[${this.variantList[0]}]`, '1');
    await this._update(params, null);
  }

  /** Reflects the SP checked state on all toggles in the current DOM */
  _syncToggles(checked) {
    document.querySelectorAll(this.toggleSelector).forEach((el) => {
      el.checked = checked;
    });
  }

  /**
   * Opens the checkout gate the moment any add-to-cart button is clicked —
   * before the drawer opens, so the gate is always set before checkout is reachable.
   */
  _onAddToCartClick(evt) {
    if (sessionStorage.getItem(SP_OPT_OUT_KEY)) return;
    if (!this.variantList.length) return;
    const path = evt.composedPath?.() ?? [evt.target];
    const isAddBtn = path.some(
      (n) => n?.nodeType === 1 && n.matches?.('button[name="add"], button.add-to-cart-button')
    );
    if (!isAddBtn) return;
    this._beginPending();
  }

  _onChange(evt) {
    const path = evt.composedPath?.() ?? [evt.target];
    const el = path.find((n) => n?.nodeType === 1 && n.matches?.(this.toggleSelector));
    if (!el) return;

    if (!this.variantList.length) this.rescan();
    const selectedId = Number(el.getAttribute('data-variantid'));
    if (!Number.isFinite(selectedId)) return;

    // Persist the customer's explicit preference for this session
    if (el.checked) {
      sessionStorage.removeItem(SP_OPT_OUT_KEY);
    } else {
      sessionStorage.setItem(SP_OPT_OUT_KEY, '1');
    }

    const params = new URLSearchParams();
    this.variantList.forEach((id) =>
      params.append(`updates[${id}]`, el.checked && id === selectedId ? '1' : '0')
    );

    this._update(params, el);
  }

  async _onCartUpdate(evt) {
    const cart = evt?.detail?.resource;
    const source = evt?.detail?.data?.source;
    if (!cart) return;

    // SP's own update fired — resolve the checkout gate and exit
    if (source === 'shipping-protection') {
      this._endPending();
      this.rescan();
      return;
    }

    // Auto-clear if SP is the only item left
    if (cart.items?.length === 1 && this.variantList.includes(cart.items[0]?.id)) {
      await this._update(null, null, true);
      this.rescan();
      return;
    }

    // Gate checkout: set _pendingAdd SYNCHRONOUSLY before any await so the
    // checkout intercept sees it even if the user clicks checkout while this
    // handler is still running async work.
    if (this.variantList.length && !sessionStorage.getItem(SP_OPT_OUT_KEY)) {
      this._beginPending();
    }

    if (cart.items) {
      // Full cart data from CartUpdateEvent — use directly, no extra fetch needed
      await this._maybeAddSP(cart);
    } else if (this.variantList.length) {
      // CartAddEvent from product form carries partial data (no items array).
      // SP toggle is in DOM — fetch full cart to determine if SP needs adding.
      await this._autoAddFromCart();
    }
    // variantList empty: SP toggle not in DOM yet — rescan() handles it after drawer renders

    // If _maybeAddSP / _autoAddFromCart decided no add was needed (cart empty, SP
    // already present, or opted-out), resolve pending now so checkout isn't blocked.
    // If SP *was* added, the CartUpdateEvent with source='shipping-protection' already
    // resolved it above via _endPending().
    this._endPending();

    this.rescan();
  }

  /** Open the checkout gate. Idempotent — second call while gate is open is a no-op. */
  _beginPending() {
    if (this._pendingAdd) return;
    this._pendingAdd = new Promise((r) => { this._resolvePending = r; });
    // Safety valve: never block checkout longer than 5 s (e.g. if SP's event never fires)
    this._pendingTimer = setTimeout(() => this._endPending(), 5000);
  }

  /** Close the checkout gate. Safe to call multiple times. */
  _endPending() {
    clearTimeout(this._pendingTimer);
    if (this._resolvePending) {
      this._resolvePending();
      this._resolvePending = null;
      this._pendingAdd = null;
    }
  }

  /**
   * If the user clicks checkout while SP auto-add is still in flight, wait for it first.
   * Only intercepts cart-form submissions triggered by the checkout button.
   */
  _interceptCheckout(evt) {
    const form = evt.target;
    if (form?.id !== 'cart-form') return;
    if (evt.submitter?.name !== 'checkout') return;
    if (!this._pendingAdd) return;

    evt.preventDefault();
    const submitter = evt.submitter;
    this._pendingAdd.finally(() => {
      form.requestSubmit(submitter);
    });
  }

  async _update(params, el, isClear = false) {
    if (el) { el.classList.add('is-loading'); el.disabled = true; }

    try {
      const endpoint = isClear ? '/cart/clear.js' : '/cart/update.js';

      // Collect all cart-items section IDs so we can request their rendered HTML
      // in the same round-trip — avoids the extra sectionRenderer.renderSection()
      // fetch that causes the drawer to flash/close.
      const sectionIds = Array.from(
        document.querySelectorAll('cart-items-component[data-section-id]'),
        (el) => el.getAttribute('data-section-id')
      ).filter(Boolean);

      let body = params ? params.toString() : '';
      if (!isClear && sectionIds.length) {
        const sep = body ? '&' : '';
        body += sep + sectionIds.map((id) => `sections[]=${encodeURIComponent(id)}`).join('&');
      }

      const opts = isClear
        ? { method: 'POST', credentials: 'same-origin' }
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            body,
            credentials: 'same-origin',
          };

      const res = await fetch(endpoint, opts);
      if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);

      // /cart/update.js returns { ..., sections: { [sectionId]: html } } when sections[] is passed.
      // /cart/clear.js always returns just the cart object.
      const payload = await res.json();
      const cart = payload.sections ? { ...payload, sections: undefined } : payload;
      const sections = payload.sections ?? {};

      document.dispatchEvent(new CartUpdateEvent(cart, 'manual-trigger', {
        itemCount: cart.item_count,
        source: 'shipping-protection',
        sections,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      if (el) { el.classList.remove('is-loading'); el.disabled = false; }
    }
  }
}

if (!window.cartShippingProtection) {
  window.cartShippingProtection = new CartShippingProtection();
}
